import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Check, X, Edit2, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FELD_LABELS = {
  name: "Name", position: "Position", stundenlohn: "Stundenlohn", wochenstunden: "Stunden/Wo.",
  anstellungsart: "Art", bezeichnung: "Bezeichnung", betrag: "Betrag", kategorie: "Kategorie",
  intervall: "Intervall", einheit: "Einheit", preis_pro_einheit: "Preis/Einheit", lieferant: "Lieferant",
  datum: "Datum", einnahmen_speisen: "Speisen", einnahmen_getraenke: "Getränke",
  einnahmen_sonstiges: "Sonstiges", gesamt: "Gesamt", zutat_name: "Artikel",
  menge: "Menge", einkaufspreis: "EK-Preis", lieferdatum: "Lieferdatum"
};

const ImportButton = ({ kategorie, felder, onImportSuccess, label = "CSV/PDF Import" }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setFileName(file.name);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("target_category", kategorie);
      const res = await axios.post(`${API}/import/analyze`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setData(res.data.vorschau || []);
      if (res.data.vorschau?.length > 0) {
        toast.success(`${res.data.erkannte_eintraege} Einträge erkannt`);
      } else {
        toast.warning("Keine passenden Daten erkannt");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Analysieren");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleConfirm = async () => {
    if (data.length === 0) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/import/confirm`, {
        kategorie, eintraege: data
      }, { withCredentials: true });
      toast.success(res.data.nachricht);
      setOpen(false);
      setData([]);
      if (onImportSuccess) onImportSuccess();
    } catch (err) {
      toast.error("Fehler beim Importieren");
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-sm text-xs" data-testid={`import-btn-${kategorie}`}>
        <Upload size={14} className="mr-1" />{label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-white dark:bg-slate-800 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-lg dark:text-slate-100">Daten importieren</DialogTitle>
            <DialogDescription className="text-xs dark:text-slate-400">CSV oder PDF hochladen, prüfen und importieren</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Input ref={fileRef} type="file" accept=".csv,.pdf" onChange={handleUpload} className="border-stone-300 dark:border-slate-600 rounded-sm" />

            {uploading && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/30 rounded text-sm">
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                <span className="dark:text-blue-300">Analysiere {fileName}...</span>
              </div>
            )}

            {data.length > 0 && (
              <>
                <div className="overflow-x-auto max-h-[350px] overflow-y-auto border border-stone-100 dark:border-slate-700 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50 dark:bg-slate-700 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium text-slate-600 dark:text-slate-300">#</th>
                        {felder.map(f => (
                          <th key={f} className="px-2 py-1.5 text-left font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">{FELD_LABELS[f] || f}</th>
                        ))}
                        <th className="px-2 py-1.5 text-right font-medium text-slate-600 dark:text-slate-300">Aktion</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100 dark:divide-slate-700">
                      {data.map((row, i) => (
                        <tr key={i} className="hover:bg-stone-50 dark:hover:bg-slate-700/50">
                          <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
                          {felder.map(f => (
                            <td key={f} className="px-2 py-1.5 dark:text-slate-300">
                              {editingRow === i ? (
                                <Input value={editValues[f] ?? ""} onChange={(e) => setEditValues({ ...editValues, [f]: e.target.value })} className="h-6 text-xs" />
                              ) : (
                                <span className="font-mono">{row[f] !== undefined ? String(row[f]) : "-"}</span>
                              )}
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-right">
                            {editingRow === i ? (
                              <div className="flex gap-1 justify-end">
                                <button onClick={() => { const u = [...data]; u[i] = {...editValues}; setData(u); setEditingRow(null); }} className="text-emerald-600"><Check size={12} /></button>
                                <button onClick={() => setEditingRow(null)} className="text-slate-400"><X size={12} /></button>
                              </div>
                            ) : (
                              <div className="flex gap-1 justify-end">
                                <button onClick={() => { setEditingRow(i); setEditValues({...row}); }} className="text-blue-500"><Edit2 size={12} /></button>
                                <button onClick={() => setData(data.filter((_,j) => j !== i))} className="text-red-400"><Trash2 size={12} /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded flex items-start gap-2">
                  <AlertTriangle size={13} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">Bitte prüfen Sie alle Einträge auf Richtigkeit.</p>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setData([]); }} className="rounded-sm text-xs">Verwerfen</Button>
                  <Button size="sm" onClick={handleConfirm} disabled={importing} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs" data-testid={`confirm-import-${kategorie}`}>
                    {importing ? <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
                    {data.length} Einträge importieren
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImportButton;
